// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🗓️ VIRTUAL TRAINING EDITOR COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  createVirtualTraining,
  updateVirtualTraining,
  getVirtualTraining,
} from '../../services/virtualTrainings';
import {
  getPersonalExercises,
  getClubExercises,
} from '../../services/exercises';
import type { VirtualTrainingWithExercises, Exercise, Difficulty } from '../../types';
import './VirtualTrainingEditor.css';

interface VirtualTrainingEditorProps {
  trainingId?: number;
  clubId?: number;
  onSave: (training: VirtualTrainingWithExercises) => void;
  onCancel: () => void;
  onDelete?: (trainingId: number) => void;
}

interface SelectedExercise {
  exercise_id: number;
  exercise?: Exercise;
  order_index: number;
  target_score?: number;
  repetitions: number;
}

export const VirtualTrainingEditor: React.FC<VirtualTrainingEditorProps> = ({
  trainingId,
  clubId: propClubId,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { player } = useAuth();
  const { t } = useTranslation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetSkillLevel, setTargetSkillLevel] = useState<Difficulty | ''>('');
  const [estimatedDuration, setEstimatedDuration] = useState<number | undefined>();
  const [selectedExercises, setSelectedExercises] = useState<SelectedExercise[]>([]);
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [trainingId, propClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      // Load available exercises
      let exercises: Exercise[] = [];
      if (propClubId) {
        exercises = await getClubExercises(propClubId);
      } else if (player) {
        exercises = await getPersonalExercises(player.id);
      }
      setAvailableExercises(exercises);

      // Load existing training if editing
      if (trainingId) {
        const training = await getVirtualTraining(trainingId);
        if (training) {
          setTitle(training.title);
          setDescription(training.description || '');
          setTargetSkillLevel((training.target_skill_level as Difficulty) || '');
          setEstimatedDuration(training.estimated_duration_minutes || undefined);
          
          // Map exercises
          const mappedExercises = (training.exercises || []).map(ex => ({
            exercise_id: ex.exercise_id,
            exercise: ex.exercise,
            order_index: ex.order_index,
            target_score: ex.target_score || undefined,
            repetitions: ex.repetitions || 1,
          }));
          setSelectedExercises(mappedExercises.sort((a, b) => a.order_index - b.order_index));
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExercise = (exerciseId: number) => {
    const exercise = availableExercises.find(ex => ex.id === exerciseId);
    if (!exercise) return;

    const newExercise: SelectedExercise = {
      exercise_id: exerciseId,
      exercise,
      order_index: selectedExercises.length + 1,
      repetitions: 1,
    };

    setSelectedExercises([...selectedExercises, newExercise]);
  };

  const handleRemoveExercise = (index: number) => {
    const updated = selectedExercises.filter((_, idx) => idx !== index);
    // Reorder
    updated.forEach((ex, idx) => {
      ex.order_index = idx + 1;
    });
    setSelectedExercises(updated);
  };

  const handleMoveExercise = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedExercises.length) return;

    const updated = [...selectedExercises];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update order indices
    updated.forEach((ex, idx) => {
      ex.order_index = idx + 1;
    });

    setSelectedExercises(updated);
  };

  const handleUpdateExercise = (index: number, field: 'repetitions' | 'target_score', value: number | undefined) => {
    const updated = [...selectedExercises];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedExercises(updated);
  };

  const handleSave = async () => {
    if (!player || !title.trim()) {
      alert(t('training.virtualTrainings.titleRequired'));
      return;
    }

    if (selectedExercises.length === 0) {
      alert(t('training.virtualTrainings.noExercises'));
      return;
    }

    setSaving(true);
    try {
      const trainingData = {
        created_by: player.id,
        club_id: propClubId,
        title: title.trim(),
        description: description.trim() || undefined,
        target_skill_level: targetSkillLevel || undefined,
        estimated_duration_minutes: estimatedDuration,
        exercises: selectedExercises.map(ex => ({
          exercise_id: ex.exercise_id,
          order_index: ex.order_index,
          target_score: ex.target_score,
          repetitions: ex.repetitions,
        })),
      };

      let result;
      if (trainingId) {
        await updateVirtualTraining(trainingId, trainingData);
        result = await getVirtualTraining(trainingId);
      } else {
        result = await createVirtualTraining(trainingData);
      }

      if (result) {
        onSave(result);
      }
    } catch (err) {
      console.error('Failed to save virtual training:', err);
      alert(t('training.virtualTrainings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const unselectedExercises = availableExercises.filter(
    ex => !selectedExercises.find(sel => sel.exercise_id === ex.id)
  );

  return (
    <div className="virtual-training-editor">
      <div className="editor-header">
        <h2>
          {trainingId 
            ? t('training.virtualTrainings.edit') 
            : t('training.virtualTrainings.create')
          }
        </h2>
      </div>

      <div className="editor-form">
        {/* Title */}
        <div className="form-group">
          <label>{t('training.virtualTrainings.title')} *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('training.virtualTrainings.titlePlaceholder')}
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label>{t('training.virtualTrainings.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('training.virtualTrainings.descriptionPlaceholder')}
            rows={3}
          />
        </div>

        <div className="form-row">
          {/* Target Skill Level */}
          <div className="form-group">
            <label>{t('training.virtualTrainings.targetSkillLevel')}</label>
            <select value={targetSkillLevel} onChange={(e) => setTargetSkillLevel(e.target.value as Difficulty)}>
              <option value="">{t('training.virtualTrainings.anyLevel')}</option>
              <option value="beginner">{t('training.difficulty.beginner')}</option>
              <option value="intermediate">{t('training.difficulty.intermediate')}</option>
              <option value="advanced">{t('training.difficulty.advanced')}</option>
              <option value="expert">{t('training.difficulty.expert')}</option>
            </select>
          </div>

          {/* Estimated Duration */}
          <div className="form-group">
            <label>{t('training.virtualTrainings.estimatedDuration')}</label>
            <input
              type="number"
              value={estimatedDuration || ''}
              onChange={(e) => setEstimatedDuration(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={t('training.virtualTrainings.minutes')}
              min="1"
            />
          </div>
        </div>

        {/* Exercises Selection */}
        <div className="exercises-section">
          <h3>{t('training.virtualTrainings.exercises')} *</h3>
          
          {/* Selected Exercises */}
          {selectedExercises.length > 0 && (
            <div className="selected-exercises">
              {selectedExercises.map((selEx, index) => (
                <div key={`${selEx.exercise_id}-${index}`} className="selected-exercise">
                  <div className="exercise-order">#{index + 1}</div>
                  <div className="exercise-info">
                    <strong>{selEx.exercise?.title || 'Unknown'}</strong>
                    {selEx.exercise?.difficulty && (
                      <span className={`difficulty-badge ${selEx.exercise.difficulty}`}>
                        {t(`training.difficulty.${selEx.exercise.difficulty}`)}
                      </span>
                    )}
                  </div>
                  <div className="exercise-controls">
                    <input
                      type="number"
                      value={selEx.repetitions}
                      onChange={(e) => handleUpdateExercise(index, 'repetitions', Number(e.target.value))}
                      min="1"
                      title={t('training.virtualTrainings.repetitions')}
                    />
                    <span>×</span>
                    <div className="move-buttons">
                      <button
                        onClick={() => handleMoveExercise(index, 'up')}
                        disabled={index === 0}
                        title={t('common.moveUp')}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleMoveExercise(index, 'down')}
                        disabled={index === selectedExercises.length - 1}
                        title={t('common.moveDown')}
                      >
                        ↓
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveExercise(index)}
                      className="btn-remove"
                      title={t('common.remove')}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Exercise Dropdown */}
          {unselectedExercises.length > 0 && (
            <div className="add-exercise">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddExercise(Number(e.target.value));
                    e.target.value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="">{t('training.virtualTrainings.addExercise')}</option>
                {unselectedExercises.map(ex => (
                  <option key={ex.id} value={ex.id}>
                    {ex.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedExercises.length === 0 && (
            <p className="hint">{t('training.virtualTrainings.noExercisesSelected')}</p>
          )}
        </div>

        {/* Actions */}
        <div className="editor-actions">
          <div className="editor-actions-left">
            {trainingId && onDelete && (
              <button 
                onClick={() => onDelete(trainingId)} 
                disabled={saving}
                className="btn-danger"
              >
                🗑️ {t('common.delete')}
              </button>
            )}
          </div>
          <div className="editor-actions-right">
            <button onClick={onCancel} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button onClick={handleSave} disabled={saving || !title.trim() || selectedExercises.length === 0} className="btn-primary">
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

